<script>
    import { geoMercator, geoPath } from 'd3-geo';
    import { onMount } from 'svelte';

    import calculateHeightRatio from '../utils/calculate-height-ratio.js';
    import scaleCanvas from '../utils/scale-canvas.js';

    export let maxHeight = 300;
    /** @type {GeoJSON.FeatureCollection} */
    export let geography;
    /** @type {GeoJSON.FeatureCollection} */
    export let cities;
    /** @type {(feature: GeoJSON.Feature) => string} */
    export let systemCoordinates;

    const projection = geoMercator();
    const canvasPath = geoPath(projection);
    const heightRatio = calculateHeightRatio(geography.bounds);
    // const isHorizontal = heightRatio < 1;
    // const margin = isHorizontal ? 20 : 5;
    const margin = 5;

    /** @type {number} */
    let width;
    /** @type {number} */
    let height;
    /** @type {number} */
    let _height;
    /** @type {HTMLDivElement} */
    let container;
    /** @type {SVGSVGElement} */
    let svg;
    /** @type {HTMLCanvasElement} */
    let canvas;
    /** @type {CanvasRenderingContext2D} */
    let context;
    /** @type {number} */
    let x;
    /** @type {number} */
    let y;
    let places = [];

    onMount(() => {
        context = /** @type {CanvasRenderingContext2D} */ (canvas.getContext('2d'));
        canvasPath.context(context);

        setDimensions();
        drawCanvas();
    });

    $: drawCanvas(systemCoordinates);

    function drawCanvas() {
        if (!context) return;
        // empty out the rect
        context.clearRect(0, 0, width, height);

        for (let idx = 0; idx < geography.bounds.features.length; idx++) {
        const feature = geography.bounds.features[idx];

        // const fillStyle = getFillStyle(feature);

        context.beginPath();
        canvasPath(feature);
        context.fillStyle = '#f1e1bf';
        context.fill();
        context.closePath();
        }

        context.beginPath();
        canvasPath(geography.borders);
        context.lineCap = 'round';
        context.lineJoin = 'round';
        context.strokeStyle = '#fff';
        context.lineWidth = 1;
        context.stroke();
        context.closePath();

        // Add system point
        const system_point = projection(systemCoordinates);

        context.beginPath();
        context.arc(
            system_point[0],
            system_point[1],
            6,
            0,
            Math.PI * 2,
            true
        );
        // context.fillStyle = '#24a4c9';
        context.fillStyle = '#565656';
        context.fill();
        context.lineWidth = 1;
        context.strokeStyle = '#000';
        context.stroke();
        context.closePath();

        // Add cities
        const rect_width = 5;
        const rect_width_halved = rect_width / 2;

        cities.bounds.features.forEach((/** @type {GeoJSON.Point} */ city) => {
            if (city.properties.city != "San Francisco" && city.properties.city != "San Diego") {
                const point = projection(city.geometry.coordinates);

                context.beginPath();
                context.fillStyle = '#000';
                context.fillRect(
                    point[0] - rect_width_halved, 
                    point[1] - rect_width_halved, 
                    rect_width, 
                    rect_width
                    );
                context.fill();
                context.closePath();

                context.beginPath();
                context.font = '11px Benton Gothic';
                context.lineWidth = 2;
                context.strokeStyle = '#f1e1bf';
                context.strokeText(city.properties.city, point[0] + 6, point[1] + 5);
                context.stroke();
                context.closePath();

                context.beginPath();
                context.font = '11px Benton Gothic';
                context.fillStyle = '#000';
                context.fillText(city.properties.city, point[0] + 6, point[1] + 5);
                context.fill();
                context.closePath();
            }
        });

    }

    function setDimensions() {
        const parent = container.parentElement;

        if (!parent) {
        return;
        }

        const bounds = parent.getBoundingClientRect();

        const _width = bounds.width;
        _height = bounds.height;
        width = _width;

        if (_height > maxHeight) {
        height = maxHeight;
        } else {
        height = _height;
        }

        projection.fitSize(
        [width, height],
        geography.bounds
        );

        places = cities.bounds.features.map((feature) => {
        const properties = feature.properties;
        return {
            point: projection(feature.geometry.coordinates).map((c) => c + margin),
            label: feature.properties.city,
            dx: properties.dx,
            dy: properties.dy,
            textAnchor: properties.text_anchor,
        };
        }); 

        // we break out of Svelte here because we cannot pre-set this
        // div
        container.style.width = `${width}px`;
        container.style.height = `${height}px`;

        // svg
        svg.setAttribute('width', width.toString());
        svg.setAttribute('height', height.toString());
        svg.setAttribute('viewBox', `0 0 ${width} ${height}`);

        // canvas
        scaleCanvas(context, width, height, margin);
    }

</script>

<div class="map" id="map"> 
    <div
        bind:this={container}
        style="position: relative; margin: auto"
    >
        <svg
            style="position: relative; top: {(_height - height)/2}px; left: 0; z-index: 2;"
            bind:this={svg}
            preserveAspectRatio="xMidYMid"
            stroke-linecap="round"
            stroke-linejoin="round"
        ></svg>
        <canvas
        style="position: absolute; top: {(_height - height)/2}px; left: 0; z-index: 1; width: 100%; height: 100%;"
        bind:this={canvas}
        />
    </div>
</div>